/**
 * ============================================================
 * ⚠️  PROTECTED FILE - DO NOT MODIFY WITHOUT APPROVAL  ⚠️
 * ============================================================
 * This is the Sale Progression / Milestones page at /sales/case/{caseId}/hub
 * Contains: Property Hub button, Price badge, H&E Removals banner,
 * Progress tracker, Timeline/Offers/Chain/Updates/Docs/Activity tabs,
 * Milestone list with update functionality, Parties panel, Key Dates
 * 
 * Layout and functionality have been approved by the client.
 * Any changes require explicit approval.
 * ============================================================
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Home, PoundSterling, Users, Clock, CheckCircle,
  AlertTriangle, Send, FileText, Loader2, Circle, Mail,
  MessageSquare, Upload, Calendar, User, Building, Briefcase,
  ClipboardList, History, CalendarDays, Truck, Search, ExternalLink, X, FileCheck, FolderOpen, Trash2, Scale, Plus, Edit2
} from "lucide-react";
import { API } from "@/App";
import { RemovalsQuoteModal } from "@/components/RemovalsQuoteModal";
import { ApplicantSearch } from "@/components/ApplicantSearch";
import { SalesEmailPanel } from "@/components/sales/SalesEmailPanel";

// Milestone permission mapping - which roles can update which milestones
// NOTE: solicitor_seller_side and solicitor_buyer_side MUST match backend (solicitor_portal.py lines 416-422)
const MILESTONE_PERMISSIONS = {
  seller_solicitor: ["management_pack", "draft_contracts_sent", "enquiries_satisfied"],
  buyer_solicitor: ["searches_contract_initiated", "searches_returned", "enquiries_raised", "file_approved", "deposit_requested"],
  broker: ["mortgage_submitted", "mortgage_offer"],
  seller: ["solicitors_instructed"],
  buyer: ["aml_checks", "solicitors_instructed", "survey_booked"],
  // External solicitor firm - aligned with backend allowed milestones
  solicitor_seller_side: ["management_pack", "draft_contracts_sent", "enquiries_satisfied", "exchange"],
  solicitor_buyer_side: ["searches_contract_initiated", "searches_returned", "enquiries_raised", "deposit_requested", "file_approved", "exchange"]
};

// Milestones that can have an ETA date (when in progress)
const ETA_MILESTONES = ["searches_contract_initiated", "searches_returned", "enquiries_raised", "enquiries_satisfied", "mortgage_offer", "file_approved"];

// Milestones that can have a scheduled/booked date
const SCHEDULED_MILESTONES = ["survey_booked", "exchange", "completion"];

// Milestones with sub-items (checkboxes)
const MILESTONES_WITH_SUBITEMS = {
  "searches_contract_initiated": [
    {id: "local_search", name: "Local Authority Search"},
    {id: "environmental_search", name: "Environmental Search"},
    {id: "water_search", name: "Water & Drainage Search"},
    {id: "contract_pack", name: "Contract Pack Received"}
  ],
  "searches_returned": [
    {id: "local_back", name: "Local Authority Search"},
    {id: "environmental_back", name: "Environmental Search"},
    {id: "water_back", name: "Water & Drainage Search"}
  ]
};

const milestoneStatusColors = {
  not_started: "bg-slate-100 text-slate-600",
  in_progress: "bg-blue-100 text-blue-700",
  done: "bg-green-100 text-green-700"
};

const roleIcons = {
  seller: Home,
  buyer: User,
  buyer_seller: Users,
  seller_solicitor: Briefcase,
  buyer_solicitor: Briefcase,
  broker: Building,
  agent: Users
};

const roleLabels = {
  seller: "Seller",
  buyer: "Buyer",
  buyer_seller: "Buyer & Seller",
  seller_solicitor: "Seller's Solicitor",
  buyer_solicitor: "Buyer's Solicitor",
  broker: "Mortgage Broker",
  agent: "Estate Agent"
};

export const CaseHubPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const reactLocation = useLocation();
  
  // Detect solicitor mode from URL param OR navigation state
  const urlParams = new URLSearchParams(reactLocation.search);
  const isSolicitorMode = urlParams.get('solicitor') === 'true' || reactLocation.state?.solicitorMode === true;
  
  // Use appropriate token based on mode
  const token = isSolicitorMode 
    ? (reactLocation.state?.solicitorToken || localStorage.getItem("solicitor_token"))
    : localStorage.getItem("token");
  const userStr = isSolicitorMode 
    ? localStorage.getItem("solicitor") 
    : localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  
  // Solicitor info for permission checks
  const solicitor = isSolicitorMode ? user : null;
  
  // Roles that can see the sales fee (staff + seller only, not solicitors)
  const canSeeSalesFee = !isSolicitorMode && user?.role && ['director', 'admin', 'sales_agent', 'sales_negotiator', 'seller'].includes(user.role);
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [parties, setParties] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerStats, setOfferStats] = useState({});
  
  // Solicitor role detection (buyer or seller solicitor)
  const [solicitorRole, setSolicitorRole] = useState(null);
  
  // Message form
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Milestone notification recipients
  const [milestoneRecipients, setMilestoneRecipients] = useState([]);
  const [milestoneSelectAll, setMilestoneSelectAll] = useState(false);
  
  // Milestone update modal
  const [milestoneModal, setMilestoneModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneStatus, setMilestoneStatus] = useState("");
  const [milestoneNotes, setMilestoneNotes] = useState("");
  const [milestoneEtaDate, setMilestoneEtaDate] = useState(null);
  const [milestoneScheduledDate, setMilestoneScheduledDate] = useState(null);
  const [milestoneSubItems, setMilestoneSubItems] = useState({});  // For tracking sub-item checkboxes
  const [etaPopoverOpen, setEtaPopoverOpen] = useState(false);
  const [scheduledPopoverOpen, setScheduledPopoverOpen] = useState(false);
  const [updatingMilestone, setUpdatingMilestone] = useState(false);
  
  // Current user info
  const [currentUser, setCurrentUser] = useState(null);
  
  // Invite modal
  const [inviteModal, setInviteModal] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [inviting, setInviting] = useState(false);

  // Invite Solicitor modal
  const [inviteSolicitorModal, setInviteSolicitorModal] = useState(false);
  const [solicitorInviteData, setSolicitorInviteData] = useState({
    type: "seller", // "seller" or "buyer"
    email: "",
    name: "",
    firm_name: "",
    phone: ""
  });
  const [invitingSolicitor, setInvitingSolicitor] = useState(false);

  // Lettings Interest State
  const [lettingsInterestSent, setLettingsInterestSent] = useState(false);

  // Removal Quote Modal State
  const [quoteModal, setQuoteModal] = useState(false);

  // EPC Upload State
  const [showEpcModal, setShowEpcModal] = useState(false);
  const [uploadingEpc, setUploadingEpc] = useState(false);
  const [epcData, setEpcData] = useState({
    rating: "",
    reference: "",
    expiry_date: "",
    file: null
  });

  // Use Template State
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [solicitorTemplates, setSolicitorTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatingDoc, setGeneratingDoc] = useState(false);

  // Applicant Search Modal
  const [showApplicantSearch, setShowApplicantSearch] = useState(false);
  
  // Applicant IDs for seller/buyer (to navigate to details)
  const [applicantIds, setApplicantIds] = useState({ seller: null, buyer: null });

  // Chain state
  const [chainProperties, setChainProperties] = useState([]);
  const [showChainModal, setShowChainModal] = useState(false);
  const [editingChainProperty, setEditingChainProperty] = useState(null);
  const [chainFormData, setChainFormData] = useState({
    property_address: "",
    property_image: "",
    position: 0,
    vendor_name: "",
    vendor_phone: "",
    vendor_email: "",
    agent: "",
    vendor_solicitor_firm: "",
    vendor_solicitor_contact: "",
    vendor_solicitor_email: "",
    vendor_solicitor_phone: "",
    price_agreed: "",
    buyer_name: "",
    buyer_phone: "",
    buyer_email: "",
    buyer_solicitor_firm: "",
    buyer_solicitor_contact: "",
    buyer_solicitor_email: "",
    buyer_solicitor_phone: "",
    buyer_chain_status: "",
    buyer_finance_status: "",
    notes: ""
  });
  const [savingChain, setSavingChain] = useState(false);

  // Memo Preview State
  const [showMemoPreview, setShowMemoPreview] = useState(false);
  const [memoLoading, setMemoLoading] = useState(false);
  const [memoDrafts, setMemoDrafts] = useState({
    seller: { content: "", email: "", name: "", approved: false, sent: false },
    buyer: { content: "", email: "", name: "", approved: false, sent: false },
    sellerSolicitor: { content: "", email: "", name: "", firm: "", approved: false, sent: false },
    buyerSolicitor: { content: "", email: "", name: "", firm: "", approved: false, sent: false }
  });
  const [sendingMemo, setSendingMemo] = useState(null);

  // Add/Edit Party State
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [partyForm, setPartyForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'buyer',
    firm_name: ''
  });
  const [savingParty, setSavingParty] = useState(false);

  useEffect(() => {
    // Check auth before fetching
    if (!token) {
      navigate(isSolicitorMode ? '/solicitor-portal/login' : '/login');
      return;
    }
    
    fetchCaseData();
    fetchChain();
    if (!isSolicitorMode) {
      fetchOffers();
    }
    
    // Get current user info based on mode
    if (isSolicitorMode) {
      const solStr = localStorage.getItem("solicitor");
      if (solStr) {
        try {
          setCurrentUser(JSON.parse(solStr));
        } catch (e) {
          console.error("Error parsing solicitor:", e);
        }
      }
    } else {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          setCurrentUser(JSON.parse(userStr));
        } catch (e) {
          console.error("Error parsing user:", e);
        }
      }
    }
  }, [caseId, isSolicitorMode, token]);

  const fetchCaseData = async () => {
    try {
      // Use solicitor API endpoint in solicitor mode
      const endpoint = isSolicitorMode 
        ? `${API}/solicitor-portal/cases/${caseId}`
        : `${API}/sales/cases/${caseId}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Solicitor API returns data in slightly different structure
      if (isSolicitorMode) {
        // Solicitor API returns case data at top level, not nested under 'case'
        // Map the response to the expected caseData format
        const solCaseData = {
          case_id: response.data.case_id,
          address_line1: response.data.address?.split(' ').slice(0, -1).join(' ') || response.data.address,
          postcode: response.data.address?.split(' ').slice(-1)[0] || '',
          offer_price: response.data.price,
          status: response.data.status,
          seller_name: response.data.parties?.seller?.name,
          buyer_name: response.data.parties?.buyer?.name,
          target_exchange_date: response.data.key_dates?.target_exchange,
          target_completion_date: response.data.key_dates?.target_completion,
          // Spread any other fields that might be present
          ...response.data
        };
        setCaseData(solCaseData);
        
        // Convert parties from nested format to array format expected by UI
        const partiesArray = [];
        if (response.data.parties?.seller?.name) {
          partiesArray.push({
            party_id: 'seller_party',
            role: 'seller',
            name: response.data.parties.seller.name,
            email: response.data.parties.seller.email,
            phone: response.data.parties.seller.phone
          });
        }
        if (response.data.parties?.buyer?.name) {
          partiesArray.push({
            party_id: 'buyer_party',
            role: 'buyer',
            name: response.data.parties.buyer.name,
            email: response.data.parties.buyer.email,
            phone: response.data.parties.buyer.phone
          });
        }
        if (response.data.parties?.seller?.solicitor?.name || response.data.parties?.seller?.solicitor?.firm) {
          partiesArray.push({
            party_id: 'seller_solicitor_party',
            role: 'seller_solicitor',
            name: response.data.parties.seller.solicitor.name || response.data.parties.seller.solicitor.firm,
            email: response.data.parties.seller.solicitor.email,
            phone: response.data.parties.seller.solicitor.phone,
            firm_name: response.data.parties.seller.solicitor.firm
          });
        }
        if (response.data.parties?.buyer?.solicitor?.name || response.data.parties?.buyer?.solicitor?.firm) {
          partiesArray.push({
            party_id: 'buyer_solicitor_party',
            role: 'buyer_solicitor',
            name: response.data.parties.buyer.solicitor.name || response.data.parties.buyer.solicitor.firm,
            email: response.data.parties.buyer.solicitor.email,
            phone: response.data.parties.buyer.solicitor.phone,
            firm_name: response.data.parties.buyer.solicitor.firm
          });
        }
        setParties(partiesArray);
        setMilestones(response.data.milestones || []);
        
        // Determine solicitor role from response
        const solRole = response.data.role || 
          (response.data.milestones?.[0]?.solicitor_role) || 
          'seller_solicitor';
        setSolicitorRole(solRole);
        
        // Solicitors don't get tasks, messages, documents, audit_log from their endpoint
        setTasks([]);
        setMessages([]);
        setDocuments([]);
        setAuditLog([]);
      } else {
        setCaseData(response.data.case);
        setParties(response.data.parties || []);
        setMilestones(response.data.milestones || []);
        setTasks(response.data.tasks || []);
        setMessages(response.data.messages || []);
        setDocuments(response.data.documents || []);
        setAuditLog(response.data.audit_log || []);
      }
      
      // Fetch applicant IDs (staff only)
      if (!isSolicitorMode) {
        try {
          const applicantsRes = await axios.get(`${API}/applicants/sales/${caseId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const applicants = applicantsRes.data.applicants || [];
          const sellerApplicant = applicants.find(a => a.role === "seller");
          const buyerApplicant = applicants.find(a => a.role === "buyer");
          setApplicantIds({
            seller: sellerApplicant?.applicant_id || null,
            buyer: buyerApplicant?.applicant_id || null
          });
        } catch (e) {
          // Applicants may not exist for older cases
          console.log("No applicants found for this case");
        }
      }
    } catch (error) {
      console.error("Error fetching case:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied - you are not assigned to this case");
        navigate(isSolicitorMode ? '/solicitor-portal' : '/sales');
      } else {
        toast.error("Failed to load case");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChain = async () => {
    try {
      const response = await axios.get(`${API}/sales/chain/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChainProperties(response.data.chain || []);
    } catch (error) {
      console.log("No chain data found");
      setChainProperties([]);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/sales-automation/offers/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data.offers || []);
      setOfferStats(response.data.stats || {});
    } catch (error) {
      console.log("No offers found");
      setOffers([]);
    }
  };

  const handleSaveChainProperty = async () => {
    setSavingChain(true);
    try {
      if (editingChainProperty) {
        // Update existing
        await axios.put(`${API}/sales/chain/${editingChainProperty.chain_id}`, chainFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Chain property updated");
      } else {
        // Add new
        await axios.post(`${API}/sales/chain`, {
          ...chainFormData,
          case_id: caseId,
          position: chainProperties.length
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Property added to chain");
      }
      setShowChainModal(false);
      setEditingChainProperty(null);
      resetChainForm();
      fetchChain();
    } catch (error) {
      console.error("Error saving chain property:", error);
      toast.error("Failed to save chain property");
    } finally {
      setSavingChain(false);
    }
  };

  const handleDeleteChainProperty = async (chainId) => {
    if (!confirm("Remove this property from the chain?")) return;
    try {
      await axios.delete(`${API}/sales/chain/${chainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property removed from chain");
      fetchChain();
    } catch (error) {
      console.error("Error deleting chain property:", error);
      toast.error("Failed to remove property");
    }
  };

  const resetChainForm = () => {
    setChainFormData({
      property_address: "",
      property_image: "",
      position: 0,
      vendor_name: "",
      vendor_phone: "",
      vendor_email: "",
      agent: "",
      vendor_solicitor_firm: "",
      vendor_solicitor_contact: "",
      vendor_solicitor_email: "",
      vendor_solicitor_phone: "",
      price_agreed: "",
      buyer_name: "",
      buyer_phone: "",
      buyer_email: "",
      buyer_solicitor_firm: "",
      buyer_solicitor_contact: "",
      buyer_solicitor_email: "",
      buyer_solicitor_phone: "",
      buyer_chain_status: "",
      buyer_finance_status: "",
      notes: ""
    });
  };

  const openEditChainModal = (property) => {
    setEditingChainProperty(property);
    setChainFormData({
      property_address: property.property_address || "",
      property_image: property.property_image || "",
      position: property.position || 0,
      vendor_name: property.vendor_name || "",
      vendor_phone: property.vendor_phone || "",
      vendor_email: property.vendor_email || "",
      agent: property.agent || "",
      vendor_solicitor_firm: property.vendor_solicitor_firm || "",
      vendor_solicitor_contact: property.vendor_solicitor_contact || "",
      vendor_solicitor_email: property.vendor_solicitor_email || "",
      vendor_solicitor_phone: property.vendor_solicitor_phone || "",
      price_agreed: property.price_agreed || "",
      buyer_name: property.buyer_name || "",
      buyer_phone: property.buyer_phone || "",
      buyer_email: property.buyer_email || "",
      buyer_solicitor_firm: property.buyer_solicitor_firm || "",
      buyer_solicitor_contact: property.buyer_solicitor_contact || "",
      buyer_solicitor_email: property.buyer_solicitor_email || "",
      buyer_solicitor_phone: property.buyer_solicitor_phone || "",
      buyer_chain_status: property.buyer_chain_status || "",
      buyer_finance_status: property.buyer_finance_status || "",
      notes: property.notes || ""
    });
    setShowChainModal(true);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    if (selectedRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/sales/messages`, {
        case_id: caseId,
        message_type: "update",
        body: newMessage,
        recipient_party_ids: selectedRecipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Update sent to ${selectedRecipients.length} recipient(s)`);
      setNewMessage("");
      setSelectedRecipients([]);
      setSelectAll(false);
      fetchCaseData();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSelectAllRecipients = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Select all parties with emails
      const allPartyIds = parties.filter(p => p.email).map(p => p.party_id);
      setSelectedRecipients(allPartyIds);
    } else {
      setSelectedRecipients([]);
    }
  };

  const toggleRecipient = (partyId) => {
    if (selectedRecipients.includes(partyId)) {
      setSelectedRecipients(selectedRecipients.filter(id => id !== partyId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedRecipients, partyId];
      setSelectedRecipients(newSelected);
      // Check if all are now selected
      const allPartyIds = parties.filter(p => p.email).map(p => p.party_id);
      if (newSelected.length === allPartyIds.length) {
        setSelectAll(true);
      }
    }
  };

  const openMilestoneModal = (milestone) => {
    setSelectedMilestone(milestone);
    setMilestoneStatus(milestone.status);
    setMilestoneNotes("");
    // Set existing dates if any
    setMilestoneEtaDate(milestone.eta_date ? new Date(milestone.eta_date) : null);
    setMilestoneScheduledDate(milestone.scheduled_date ? new Date(milestone.scheduled_date) : null);
    // Initialize sub-items from milestone data
    setMilestoneSubItems(milestone.sub_items_completed || {});
    
    // Auto-select notification recipients: Estate Agent + milestone owner
    // Reset selection each time modal opens (no carry-over)
    const autoSelected = [];
    
    // Always preselect Estate Agent (role "agent") if they have an email
    const agentParty = parties.find(p => p.role === "agent" && p.email);
    if (agentParty) {
      autoSelected.push(agentParty.party_id);
    }
    
    // Preselect party matching milestone.owner_role if they have an email
    // Skip combined roles like "buyer_seller" - only agent gets selected in that case
    const ownerRole = milestone.owner_role;
    const isCombinedRole = ownerRole === "buyer_seller" || ownerRole === "buyer_and_seller";
    
    if (!isCombinedRole && ownerRole) {
      const ownerParty = parties.find(p => p.role === ownerRole && p.email);
      if (ownerParty && !autoSelected.includes(ownerParty.party_id)) {
        autoSelected.push(ownerParty.party_id);
      }
    }
    
    setMilestoneRecipients(autoSelected);
    setMilestoneSelectAll(false); // Do NOT auto-select "Notify All Parties"
    setMilestoneModal(true);
  };

  // Check if milestone has sub-items
  const hasSubItems = (milestoneType) => {
    return MILESTONES_WITH_SUBITEMS[milestoneType] !== undefined;
  };

  // Get sub-items for a milestone type
  const getSubItems = (milestoneType) => {
    return MILESTONES_WITH_SUBITEMS[milestoneType] || [];
  };

  // Toggle sub-item checkbox
  const toggleSubItem = (subItemId) => {
    setMilestoneSubItems(prev => ({
      ...prev,
      [subItemId]: !prev[subItemId]
    }));
  };

  // Calculate status based on sub-items for searches_back
  const calculateSubItemStatus = (milestoneType, subItemsState) => {
    const subItems = MILESTONES_WITH_SUBITEMS[milestoneType];
    if (!subItems) return null;
    
    const completedCount = subItems.filter(item => subItemsState[item.id]).length;
    if (completedCount === 0) return "not_started";
    if (completedCount === subItems.length) return "done";
    return "in_progress";
  };

  // Check if current user can update a specific milestone
  const canUpdateMilestone = (milestone) => {
    // Solicitor mode - use solicitor permissions
    if (isSolicitorMode && solicitor) {
      // Solicitors can only update specific milestone types based on their role
      const solicitorMilestoneTypes = solicitorRole === 'buyer_solicitor'
        ? MILESTONE_PERMISSIONS.solicitor_buyer_side
        : MILESTONE_PERMISSIONS.solicitor_seller_side;
      
      return solicitorMilestoneTypes && solicitorMilestoneTypes.includes(milestone.type);
    }
    
    if (!currentUser) return false;
    
    // Admins, directors, sales agents, sales negotiators (Tracey) can update anything
    const adminRoles = ["admin", "director", "sales_agent", "sales_negotiator", "lettings_manager"];
    if (adminRoles.includes(currentUser.role)) return true;
    
    // For external solicitor firm users, check which side they represent
    if (currentUser.role === "solicitor" && currentUser.firm_name) {
      // Check if this firm is the seller or buyer solicitor for this case
      const sellerSolicitor = parties.find(p => p.role === "seller_solicitor");
      const buyerSolicitor = parties.find(p => p.role === "buyer_solicitor");
      
      const isSellerSide = sellerSolicitor && 
        (sellerSolicitor.firm_name || sellerSolicitor.company || sellerSolicitor.name || "")
          .toLowerCase().includes(currentUser.firm_name.toLowerCase().split(" ")[0]);
      
      const isBuyerSide = buyerSolicitor && 
        (buyerSolicitor.firm_name || buyerSolicitor.company || buyerSolicitor.name || "")
          .toLowerCase().includes(currentUser.firm_name.toLowerCase().split(" ")[0]);
      
      if (isSellerSide) {
        const allowedMilestones = MILESTONE_PERMISSIONS.solicitor_seller_side;
        if (allowedMilestones && allowedMilestones.includes(milestone.type)) return true;
      }
      
      if (isBuyerSide) {
        const allowedMilestones = MILESTONE_PERMISSIONS.solicitor_buyer_side;
        if (allowedMilestones && allowedMilestones.includes(milestone.type)) return true;
      }
      
      return false;
    }
    
    // Check role-based permissions for other external parties
    const allowedMilestones = MILESTONE_PERMISSIONS[currentUser.role];
    if (allowedMilestones && allowedMilestones.includes(milestone.type)) {
      return true;
    }
    
    return false;
  };

  // Check if milestone should show ETA date option
  const showEtaOption = (milestone) => {
    return ETA_MILESTONES.includes(milestone?.type);
  };

  // Check if milestone should show scheduled date option
  const showScheduledOption = (milestone) => {
    return SCHEDULED_MILESTONES.includes(milestone?.type);
  };

  const handleUpdateMilestone = async () => {
    if (!selectedMilestone) return;
    
    setUpdatingMilestone(true);
    try {
      // For milestones with sub-items (like searches_back), auto-calculate status
      let finalStatus = milestoneStatus;
      if (selectedMilestone.type === "searches_back") {
        finalStatus = calculateSubItemStatus("searches_back", milestoneSubItems) || milestoneStatus;
      }

      // Use solicitor API endpoint in solicitor mode
      if (isSolicitorMode) {
        // Solicitor mode: use solicitor_token directly from localStorage (source of truth)
        const solicitorToken = localStorage.getItem("solicitor_token");
        const payload = {
          milestone_id: selectedMilestone.milestone_id,
          milestone_type: selectedMilestone.type,
          status: finalStatus,
          notes: milestoneNotes || null
        };
        
        await axios.put(`${API}/solicitor-portal/cases/${caseId}/milestone`, payload, {
          headers: { Authorization: `Bearer ${solicitorToken}` }
        });
      } else {
        // Staff mode: use staff token
        const staffToken = localStorage.getItem("token");
        const payload = {
          status: finalStatus,
          notes: milestoneNotes || null,
          notify_party_ids: milestoneRecipients,
          eta_date: milestoneEtaDate ? format(milestoneEtaDate, "yyyy-MM-dd") : null,
          scheduled_date: milestoneScheduledDate ? format(milestoneScheduledDate, "yyyy-MM-dd") : null,
          sub_items_completed: Object.keys(milestoneSubItems).length > 0 ? milestoneSubItems : null
        };
        
        await axios.patch(`${API}/sales/milestones/${selectedMilestone.milestone_id}`, payload, {
          headers: { Authorization: `Bearer ${staffToken}` }
        });
      }
      
      const notifyCount = milestoneRecipients.length;
      toast.success(notifyCount > 0 && !isSolicitorMode
        ? `Milestone updated - ${notifyCount} notified` 
        : "Milestone updated"
      );
      setMilestoneModal(false);
      setMilestoneRecipients([]);
      setMilestoneSelectAll(false);
      setMilestoneEtaDate(null);
      setMilestoneScheduledDate(null);
      fetchCaseData();
    } catch (error) {
      // Handle specific error codes
      const status = error.response?.status;
      let errorMsg;
      if (status === 403) {
        errorMsg = "You're not permitted to update this milestone.";
      } else if (status === 401) {
        errorMsg = "Session expired — please log in again.";
      } else {
        errorMsg = error.response?.data?.detail || "Failed to update milestone";
      }
      toast.error(errorMsg);
    } finally {
      setUpdatingMilestone(false);
    }
  };

  const handleMilestoneSelectAll = (checked) => {
    setMilestoneSelectAll(checked);
    if (checked) {
      const allPartyIds = parties.filter(p => p.email).map(p => p.party_id);
      setMilestoneRecipients(allPartyIds);
    } else {
      setMilestoneRecipients([]);
    }
  };

  const toggleMilestoneRecipient = (partyId) => {
    if (milestoneRecipients.includes(partyId)) {
      setMilestoneRecipients(milestoneRecipients.filter(id => id !== partyId));
      setMilestoneSelectAll(false);
    } else {
      const newSelected = [...milestoneRecipients, partyId];
      setMilestoneRecipients(newSelected);
      const allPartyIds = parties.filter(p => p.email).map(p => p.party_id);
      if (newSelected.length === allPartyIds.length) {
        setMilestoneSelectAll(true);
      }
    }
  };

  const handleInviteParty = async () => {
    if (!selectedParty) return;
    
    setInviting(true);
    try {
      await axios.post(`${API}/sales/invite/${selectedParty.party_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite sent to ${selectedParty.email}`);
      setInviteModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  // ======= INVITE SOLICITOR =======
  const handleInviteSolicitor = async () => {
    if (!solicitorInviteData.email) {
      toast.error("Please enter an email address");
      return;
    }
    
    setInvitingSolicitor(true);
    try {
      await axios.post(`${API}/sales/solicitor/invite`, {
        case_id: caseId,
        solicitor_type: solicitorInviteData.type, // "seller" or "buyer"
        email: solicitorInviteData.email,
        name: solicitorInviteData.name,
        firm_name: solicitorInviteData.firm_name,
        phone: solicitorInviteData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${solicitorInviteData.email}`);
      setInviteSolicitorModal(false);
      setSolicitorInviteData({ type: "seller", email: "", name: "", firm_name: "", phone: "" });
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send solicitor invite");
    } finally {
      setInvitingSolicitor(false);
    }
  };

  // ======= OFFER MANAGEMENT =======
  const handleAcceptOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to accept this offer? All other offers will be rejected.")) {
      return;
    }
    
    try {
      await axios.post(`${API}/sales-automation/offers/accept`, {
        offer_id: offerId,
        notes: "Accepted via CaseHub"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offer accepted! Buyer has been notified.");
      fetchOffers();
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept offer");
    }
  };

  const handleRejectOffer = async (offerId, reason = "") => {
    if (!window.confirm("Are you sure you want to reject this offer?")) {
      return;
    }
    
    try {
      await axios.post(`${API}/sales-automation/offers/reject`, {
        offer_id: offerId,
        reason: reason || "Offer rejected"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offer rejected");
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject offer");
    }
  };

  // ======= MEMO OF SALE =======
  const generateMemoDrafts = async () => {
    setMemoLoading(true);
    try {
      const seller = parties.find(p => p.role === "seller" || p.role === "vendor");
      const buyer = parties.find(p => p.role === "buyer");
      const sellerSolicitor = parties.find(p => p.role === "seller_solicitor");
      const buyerSolicitor = parties.find(p => p.role === "buyer_solicitor");
      
      const propertyAddress = `${caseData?.address_line1 || ""}, ${caseData?.postcode || ""}`;
      const acceptedOffer = offers.find(o => o.status === "accepted");
      const offerPrice = acceptedOffer?.offer_amount || caseData?.offer_price || 0;
      const formattedDate = format(new Date(), "dd MMMM yyyy");
      
      // Generate draft memos for each party
      const drafts = {
        seller: {
          name: seller?.name || "Seller",
          email: seller?.email || "",
          approved: false,
          sent: false,
          content: `Dear ${seller?.name || "Seller"},

RE: Sale of ${propertyAddress}
Sale Price: £${offerPrice?.toLocaleString()}

We are pleased to confirm that we have agreed a sale of the above property on your behalf.

The purchaser is: ${buyer?.name || "TBC"}

Your solicitor: ${sellerSolicitor?.name || "TBC"} at ${sellerSolicitor?.firm_name || "TBC"}
Purchaser's solicitor: ${buyerSolicitor?.name || "TBC"} at ${buyerSolicitor?.firm_name || "TBC"}

We will now instruct both solicitors to proceed with the transaction. You will be contacted by your solicitor shortly to begin the legal process.

If you have any questions, please do not hesitate to contact us.

Kind regards,
Goodacres Residential
${formattedDate}`
        },
        buyer: {
          name: buyer?.name || "Buyer",
          email: buyer?.email || "",
          approved: false,
          sent: false,
          content: `Dear ${buyer?.name || "Buyer"},

RE: Purchase of ${propertyAddress}
Purchase Price: £${offerPrice?.toLocaleString()}

We are pleased to confirm that your offer on the above property has been accepted.

The vendor is: ${seller?.name || "TBC"}

Your solicitor: ${buyerSolicitor?.name || "TBC"} at ${buyerSolicitor?.firm_name || "TBC"}
Vendor's solicitor: ${sellerSolicitor?.name || "TBC"} at ${sellerSolicitor?.firm_name || "TBC"}

We will now instruct both solicitors to proceed with the transaction. Please ensure you have provided your solicitor's details and any required documentation.

If you have any questions, please do not hesitate to contact us.

Kind regards,
Goodacres Residential
${formattedDate}`
        },
        sellerSolicitor: {
          name: sellerSolicitor?.name || "Seller's Solicitor",
          email: sellerSolicitor?.email || "",
          firm: sellerSolicitor?.firm_name || "",
          approved: false,
          sent: false,
          content: `Dear ${sellerSolicitor?.name || "Sirs"},

RE: ${propertyAddress}
Our Client: ${seller?.name || "TBC"} (Vendor)
Sale Price: £${offerPrice?.toLocaleString()}

We write to confirm that the above property has been sold subject to contract.

VENDOR DETAILS:
Name: ${seller?.name || "TBC"}
Address: ${seller?.address || "TBC"}
Email: ${seller?.email || "TBC"}
Phone: ${seller?.phone || "TBC"}

PURCHASER DETAILS:
Name: ${buyer?.name || "TBC"}
Email: ${buyer?.email || "TBC"}
Phone: ${buyer?.phone || "TBC"}

PURCHASER'S SOLICITORS:
Firm: ${buyerSolicitor?.firm_name || "TBC"}
Contact: ${buyerSolicitor?.name || "TBC"}
Email: ${buyerSolicitor?.email || "TBC"}
Phone: ${buyerSolicitor?.phone || "TBC"}

Please proceed with the transaction and keep us informed of progress.

Kind regards,
Goodacres Residential
${formattedDate}`
        },
        buyerSolicitor: {
          name: buyerSolicitor?.name || "Buyer's Solicitor",
          email: buyerSolicitor?.email || "",
          firm: buyerSolicitor?.firm_name || "",
          approved: false,
          sent: false,
          content: `Dear ${buyerSolicitor?.name || "Sirs"},

RE: ${propertyAddress}
Our Client: ${buyer?.name || "TBC"} (Purchaser)
Purchase Price: £${offerPrice?.toLocaleString()}

We write to confirm that the above property has been sold subject to contract.

PURCHASER DETAILS:
Name: ${buyer?.name || "TBC"}
Email: ${buyer?.email || "TBC"}
Phone: ${buyer?.phone || "TBC"}

VENDOR DETAILS:
Name: ${seller?.name || "TBC"}
Address: ${seller?.address || "TBC"}
Email: ${seller?.email || "TBC"}
Phone: ${seller?.phone || "TBC"}

VENDOR'S SOLICITORS:
Firm: ${sellerSolicitor?.firm_name || "TBC"}
Contact: ${sellerSolicitor?.name || "TBC"}
Email: ${sellerSolicitor?.email || "TBC"}
Phone: ${sellerSolicitor?.phone || "TBC"}

Please proceed with the transaction and keep us informed of progress.

Kind regards,
Goodacres Residential
${formattedDate}`
        }
      };
      
      setMemoDrafts(drafts);
      setShowMemoPreview(true);
    } catch (error) {
      toast.error("Failed to generate memo drafts");
    } finally {
      setMemoLoading(false);
    }
  };

  const handleSendMemo = async (partyType) => {
    const draft = memoDrafts[partyType];
    if (!draft.email) {
      toast.error(`No email address for ${draft.name}`);
      return;
    }
    
    setSendingMemo(partyType);
    try {
      await axios.post(`${API}/sales-automation/send-memo`, {
        case_id: caseId,
        recipient_type: partyType,
        recipient_name: draft.name,
        recipient_email: draft.email,
        memo_content: draft.content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMemoDrafts(prev => ({
        ...prev,
        [partyType]: { ...prev[partyType], sent: true, approved: true }
      }));
      
      toast.success(`Memo sent to ${draft.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send memo to ${draft.name}`);
    } finally {
      setSendingMemo(null);
    }
  };

  const handleSendAllMemos = async () => {
    const partiesToSend = Object.entries(memoDrafts)
      .filter(([_, draft]) => draft.email && draft.approved && !draft.sent);
    
    if (partiesToSend.length === 0) {
      toast.error("No memos approved for sending");
      return;
    }
    
    for (const [partyType, _] of partiesToSend) {
      await handleSendMemo(partyType);
    }
    
    // Mark case as memo issued
    try {
      await axios.post(`${API}/sales/cases/${caseId}/memo-issued`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCaseData();
    } catch (error) {
      console.error("Failed to update memo status");
    }
  };

  const handleIssueMemo = async () => {
    // Instead of sending directly, show the preview modal
    generateMemoDrafts();
  };

  // ======= PORTAL INVITES =======
  const handleInviteToPortal = async (party) => {
    if (!party.email) {
      toast.error("This party has no email address");
      return;
    }
    
    if (!window.confirm(`Send portal invite to ${party.name} (${party.email})?`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/sales-automation/invite-to-portal`, {
        case_id: caseId,
        party_id: party.party_id,
        role: party.role
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Portal invite sent to ${party.name}`);
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    }
  };

  // ======= ADD/EDIT PARTY =======
  const openAddPartyModal = () => {
    setEditingParty(null);
    setPartyForm({ name: '', email: '', phone: '', role: 'buyer', firm_name: '' });
    setShowPartyModal(true);
  };

  const openEditPartyModal = (party) => {
    setEditingParty(party);
    setPartyForm({
      name: party.name || '',
      email: party.email || '',
      phone: party.phone || '',
      role: party.role || 'buyer',
      firm_name: party.firm_name || ''
    });
    setShowPartyModal(true);
  };

  const handleSaveParty = async () => {
    if (!partyForm.name) {
      toast.error("Please enter a name");
      return;
    }
    
    setSavingParty(true);
    try {
      if (editingParty) {
        // Update existing party
        await axios.put(`${API}/sales/parties/${editingParty.party_id}`, partyForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Party updated");
      } else {
        // Add new party
        await axios.post(`${API}/sales/cases/${caseId}/parties`, partyForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Party added");
      }
      setShowPartyModal(false);
      fetchCaseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save party");
    } finally {
      setSavingParty(false);
    }
  };

  // ======= LETTINGS INTEREST =======
  const handleLettingsInterest = async () => {
    try {
      // Find buyer from parties
      const buyer = parties.find(p => p.role === "buyer");
      
      const payload = {
        case_id: caseId,
        property_address: caseData?.address_line1 || "",
        postcode: caseData?.postcode || "",
        offer_price: caseData?.offer_price || null,
        property_type: caseData?.property_type || "",
        bedrooms: caseData?.bedrooms || null,
        buyer_name: buyer?.name || currentUser?.name || "",
        buyer_email: buyer?.email || currentUser?.email || "",
        buyer_phone: buyer?.phone || ""
      };
      
      const token = localStorage.getItem("token");
      await axios.post(`${API}/lettings-interest`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLettingsInterestSent(true);
      toast.success("Thanks! Our lettings team will be in touch shortly.");
    } catch (error) {
      console.error("Lettings interest error:", error);
      toast.error("Failed to register interest. Please try again.");
    }
  };

  // Handle EPC Upload
  const handleEpcUpload = async () => {
    if (!epcData.file || !epcData.rating) {
      toast.error("Please select an EPC rating and upload a file");
      return;
    }
    
    setUploadingEpc(true);
    try {
      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("epc_rating", epcData.rating);
      formData.append("epc_reference", epcData.reference || "");
      formData.append("expiry_date", epcData.expiry_date || "");
      formData.append("file", epcData.file);
      
      await axios.post(`${API}/documents/epc/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("EPC certificate uploaded successfully");
      setShowEpcModal(false);
      setEpcData({ rating: "", reference: "", expiry_date: "", file: null });
      fetchCaseData(); // Refresh to show new EPC data
    } catch (error) {
      console.error("EPC upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload EPC certificate");
    } finally {
      setUploadingEpc(false);
    }
  };

  // View/Download document with authentication
  const handleViewDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/sales/documents/view/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create a blob URL and open it
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error("View document error:", error);
      toast.error(error.response?.data?.detail || "Failed to view document");
    }
  };

  // Delete document
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }
    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchCaseData(); // Refresh documents list
    } catch (error) {
      console.error("Delete document error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };

  // Fetch solicitor templates
  const fetchSolicitorTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API}/documents/solicitor-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSolicitorTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Open template modal
  const handleOpenTemplateModal = () => {
    fetchSolicitorTemplates();
    setShowTemplateModal(true);
  };

  // Generate document from template
  const handleUseTemplate = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }
    
    setGeneratingDoc(true);
    try {
      // Get case parties
      const seller = parties.find(p => p.role === "seller");
      const buyer = parties.find(p => p.role === "buyer");
      const sellerSolicitor = parties.find(p => p.role === "seller_solicitor");
      const buyerSolicitor = parties.find(p => p.role === "buyer_solicitor");
      
      // Create document record with case data
      const docData = {
        case_id: caseId,
        template_id: selectedTemplate.template_id,
        template_name: selectedTemplate.name,
        name: `${selectedTemplate.name} - ${caseData?.address_line1 || 'Property'}`,
        folder: selectedTemplate.category,
        status: "draft",
        case_data: {
          property_address: caseData?.address_line1 || "",
          postcode: caseData?.postcode || "",
          price: caseData?.price || caseData?.offer_price || "",
          property_type: caseData?.property_type || "",
          bedrooms: caseData?.bedrooms || "",
          epc_rating: caseData?.epc_band || "",
          seller_name: seller?.name || "",
          seller_email: seller?.email || "",
          seller_phone: seller?.phone || "",
          buyer_name: buyer?.name || "",
          buyer_email: buyer?.email || "",
          buyer_phone: buyer?.phone || "",
          seller_solicitor_firm: sellerSolicitor?.firm_name || sellerSolicitor?.name || "",
          seller_solicitor_email: sellerSolicitor?.email || "",
          buyer_solicitor_firm: buyerSolicitor?.firm_name || buyerSolicitor?.name || "",
          buyer_solicitor_email: buyerSolicitor?.email || "",
          created_date: new Date().toLocaleDateString('en-GB')
        }
      };
      
      await axios.post(`${API}/documents/from-template`, docData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Document created from "${selectedTemplate.name}" template`);
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      fetchCaseData(); // Refresh documents
    } catch (error) {
      console.error("Error using template:", error);
      toast.error(error.response?.data?.detail || "Failed to create document from template");
    } finally {
      setGeneratingDoc(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Case not found</p>
      </div>
    );
  }

  const completedMilestones = milestones.filter(m => m.status === "done").length;
  const progressPercent = (completedMilestones / milestones.length) * 100;
  
  // Check if current user is a solicitor (external firm)
  const isSolicitor = currentUser?.role === "solicitor";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => {
                // Solicitor mode goes back to solicitor portal
                if (isSolicitorMode) {
                  navigate("/solicitor-portal");
                // Sales negotiators go back to their clean dashboard
                } else if (currentUser?.role === "sales_negotiator") {
                  navigate("/progression");
                } else if (isSolicitor) {
                  navigate(`/sales/solicitor/${encodeURIComponent(currentUser.firm_name)}`);
                } else {
                  navigate("/sales");
                }
              }}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{caseData.address_line1}</h1>
                <p className="text-sm text-muted-foreground">{caseData.postcode}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Issue Memo Button - Prominent for sales_negotiator (Tracey) - hidden in solicitor mode */}
              {!isSolicitorMode && currentUser?.role === "sales_negotiator" && !caseData?.memo_issued && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleIssueMemo}
                  data-testid="issue-memo-header-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Issue Memo of Sale
                </Button>
              )}
              {!isSolicitorMode && currentUser?.role === "sales_negotiator" && caseData?.memo_issued && (
                <Badge className="bg-green-100 text-green-700 px-3 py-1">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Memo Issued
                </Badge>
              )}
              {!isSolicitorMode && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/sales/property/${caseId}`)}
                  data-testid="property-hub-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Property Hub
                </Button>
              )}
              <Badge className="text-lg px-3 py-1 bg-emerald-100 text-emerald-700">
                <PoundSterling className="w-4 h-4 mr-1" />
                £{caseData.offer_price?.toLocaleString()}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* H&E Removals Banner - TOP OF PAGE */}
        <Card className="mb-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0 overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_estate-collab/artifacts/qo5gj71j_de23a5_ef0c05dc2afd4b4d87563448ca96f4be~mv2.jpg" 
                  alt="H&E Removals"
                  className="w-16 h-16 object-contain rounded-lg bg-white/10 p-1"
                />
                <div>
                  <h3 className="text-lg font-semibold">Need a Removal Company?</h3>
                  <p className="text-emerald-100 text-sm mt-0.5">
                    H&E Removals - Professional, reliable home moving services
                  </p>
                </div>
              </div>
              <Button 
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-6 shadow-lg"
                onClick={() => setQuoteModal(true)}
                data-testid="get-instant-quote-btn"
              >
                Get Instant Quote
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lettings Cross-Sell Banner */}
        <Card className="mb-6 bg-black text-white border-0 overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-4">
                <img 
                  src="/goodacres-g-logo.png" 
                  alt="Goodacres" 
                  className="w-14 h-14 rounded object-contain"
                />
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    Buying as an Investment?
                    <Badge className="bg-yellow-400 text-yellow-900 text-xs">Landlords</Badge>
                  </h3>
                  <p className="text-slate-300 text-sm mt-0.5">
                    Let Goodacres Lettings manage your property - maximise returns, minimise hassle
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="border-slate-500 text-white hover:bg-slate-800 font-medium"
                  onClick={() => window.open('/landlord-demo', '_blank')}
                  data-testid="lettings-learn-more-btn"
                >
                  Find out what we offer
                </Button>
                <Button 
                  className="bg-yellow-400 text-black hover:bg-yellow-300 font-semibold px-6 shadow-lg"
                  onClick={handleLettingsInterest}
                  disabled={lettingsInterestSent}
                  data-testid="lettings-interest-btn"
                >
                  {lettingsInterestSent ? "We'll Be In Touch!" : "Want us to contact you?"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Bar */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Progress</span>
                  <span className="text-sm text-muted-foreground">
                    {completedMilestones} of {milestones.length} milestones
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="milestones">
              <TabsList className="grid grid-cols-6">
                <TabsTrigger value="milestones">
                  <ClipboardList className="w-4 h-4 mr-1" />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="offers">
                  <PoundSterling className="w-4 h-4 mr-1" />
                  Offers ({offers.length})
                </TabsTrigger>
                <TabsTrigger value="chain">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Chain
                </TabsTrigger>
                <TabsTrigger value="messages">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Updates ({messages.length})
                </TabsTrigger>
                <TabsTrigger value="documents">
                  <FileText className="w-4 h-4 mr-1" />
                  Docs ({documents.length})
                </TabsTrigger>
                <TabsTrigger value="activity">
                  <History className="w-4 h-4 mr-1" />
                  Activity
                </TabsTrigger>
              </TabsList>

              {/* Offers Tab */}
              <TabsContent value="offers" className="mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <PoundSterling className="w-5 h-5" />
                        Offer Stack
                      </CardTitle>
                      {offerStats.highest_offer > 0 && (
                        <Badge className="bg-green-600">
                          Highest: £{offerStats.highest_offer?.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    {offerStats.total > 0 && (
                      <p className="text-sm text-slate-500">
                        {offerStats.submitted} pending • {offerStats.accepted} accepted • {offerStats.rejected} rejected
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {offers.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <PoundSterling className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No offers received yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {offers.map((offer) => (
                          <div
                            key={offer.offer_id}
                            className={`p-4 rounded-lg border ${
                              offer.status === "accepted" 
                                ? "bg-green-50 border-green-200" 
                                : offer.status === "rejected"
                                ? "bg-red-50 border-red-200"
                                : "bg-white"
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xl font-bold">
                                    £{offer.offer_amount?.toLocaleString()}
                                  </span>
                                  <Badge variant={
                                    offer.status === "accepted" ? "default" :
                                    offer.status === "rejected" ? "destructive" :
                                    "secondary"
                                  }>
                                    {offer.status}
                                  </Badge>
                                </div>
                                <p className="font-medium mt-1">{offer.applicant_name}</p>
                                <div className="text-sm text-slate-500 space-y-1 mt-2">
                                  <p>📧 {offer.applicant_email}</p>
                                  {offer.applicant_phone && <p>📞 {offer.applicant_phone}</p>}
                                  <p>💰 {offer.funding_type === "cash" ? "Cash buyer" : "Mortgage"}</p>
                                  <p>🔗 {offer.chain_status === "no_chain" ? "No chain" : offer.chain_status}</p>
                                  {offer.completion_timeframe && (
                                    <p>📅 {offer.completion_timeframe.replace("_", " ")}</p>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-2">
                                  Submitted {new Date(offer.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              
                              {offer.status === "submitted" && (
                                <div className="flex flex-col gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleAcceptOffer(offer.offer_id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => handleRejectOffer(offer.offer_id)}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                              
                              {offer.status === "accepted" && (
                                <CheckCircle className="w-8 h-8 text-green-600" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Issue Memo of Sale Button - Show for sales_negotiator (Tracey) or when offer accepted */}
                    {(currentUser?.role === "sales_negotiator" || offers.some(o => o.status === "accepted")) && !caseData?.memo_issued && (
                      <div className="mt-6 pt-4 border-t">
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={handleIssueMemo}
                          data-testid="issue-memo-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Issue Memo of Sale
                        </Button>
                        <p className="text-xs text-slate-500 text-center mt-2">
                          Review and send memos to buyer, vendor, and solicitors
                        </p>
                      </div>
                    )}
                    
                    {caseData?.memo_issued && (
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Memo of Sale Issued</span>
                        </div>
                        {caseData?.memo_issued_at && (
                          <p className="text-xs text-slate-500 text-center mt-1">
                            {new Date(caseData.memo_issued_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Milestones Timeline */}
              <TabsContent value="milestones" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {milestones.map((milestone, idx) => {
                        const canUpdate = canUpdateMilestone(milestone);
                        return (
                        <div 
                          key={milestone.milestone_id}
                          className={`flex items-start gap-4 p-2 rounded-lg transition-colors ${
                            canUpdate ? "cursor-pointer hover:bg-slate-50" : "opacity-80"
                          }`}
                          onClick={() => canUpdate && openMilestoneModal(milestone)}
                          data-testid={`milestone-${milestone.type}`}
                        >
                          <div className="flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              milestone.status === "done" 
                                ? "bg-green-100" 
                                : milestone.status === "in_progress"
                                ? "bg-blue-100"
                                : "bg-slate-100"
                            }`}>
                              {milestone.status === "done" ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : milestone.status === "in_progress" ? (
                                <Clock className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            {idx < milestones.length - 1 && (
                              <div className={`w-0.5 h-8 ${
                                milestone.status === "done" ? "bg-green-200" : "bg-slate-200"
                              }`} />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${
                                milestone.status === "done" ? "text-green-700" : ""
                              }`}>
                                {milestone.name}
                                {canUpdate && (
                                  <span className="ml-2 text-xs text-primary font-normal">(click to update)</span>
                                )}
                              </span>
                              <Badge className={milestoneStatusColors[milestone.status || "not_started"]}>
                                {(milestone.status || "not_started").replace("_", " ")}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Owner: {roleLabels[milestone.owner_role]}
                            </p>
                            {/* Show sub-items if present */}
                            {hasSubItems(milestone.type) && (
                              <div className="mt-2 pl-2 border-l-2 border-slate-200">
                                {getSubItems(milestone.type).map((item) => (
                                  <div key={item.id} className="flex items-center gap-2 text-xs text-slate-600">
                                    {milestone.sub_items_completed?.[item.id] ? (
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                    ) : (
                                      <Circle className="w-3 h-3 text-slate-300" />
                                    )}
                                    <span className={milestone.sub_items_completed?.[item.id] ? "text-green-600" : ""}>
                                      {item.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Show scheduled date with TBC option */}
                            {SCHEDULED_MILESTONES.includes(milestone.type) && (
                              <p className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                                <Calendar className="w-3 h-3" />
                                {milestone.scheduled_date 
                                  ? new Date(milestone.scheduled_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                                  : "Date: TBC"}
                              </p>
                            )}
                            {/* Show ETA date if set and in progress */}
                            {milestone.eta_date && milestone.status === "in_progress" && (
                              <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                                <CalendarDays className="w-3 h-3" />
                                ETA: {new Date(milestone.eta_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              </p>
                            )}
                            {/* Show last updated by if external party */}
                            {milestone.last_updated_by_name && milestone.last_updated_by_role && 
                              !["admin", "director", "sales_agent"].includes(milestone.last_updated_by_role) && (
                              <p className="text-xs text-amber-600 mt-1">
                                Updated by: {milestone.last_updated_by_name}
                              </p>
                            )}
                            {milestone.completed_at && (
                              <p className="text-xs text-green-600">
                                Completed: {formatDate(milestone.completed_at)}
                              </p>
                            )}
                          </div>
                        </div>
                      )})}
                    </div>
                  </CardContent>
                </Card>

              </TabsContent>

              {/* Chain Tab */}
              <TabsContent value="chain" className="mt-4">
                <Card>
                  <CardContent className="py-6">
                    {/* Add Property Button at Top - Navigate to Chain Builder */}
                    <div className="flex justify-center mb-6">
                      <Button 
                        onClick={() => navigate(`/sales/case/${caseId}/chain`)} 
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6"
                        data-testid="add-chain-property-btn"
                      >
                        + Add Property
                      </Button>
                    </div>
                    
                    {chainProperties.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <ExternalLink className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No chain properties added yet</p>
                        <p className="text-sm mt-1">Click &quot;Add Property&quot; to open the Chain Builder</p>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Vertical connector line */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 -translate-x-1/2 z-0" />
                        
                        {/* Chain Properties - Top to bottom */}
                        <div className="relative z-10 space-y-0">
                          {chainProperties.sort((a, b) => a.position - b.position).map((property, idx, arr) => (
                            <div key={property.chain_id || idx} className="relative">
                              {/* Connector Arrow Down */}
                              {idx > 0 && (
                                <div className="flex justify-center py-2">
                                  <div className="flex flex-col items-center">
                                    <div className="w-0.5 h-6 bg-slate-300"></div>
                                    <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-slate-300"></div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Property Card */}
                              <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                                {/* Header with Address and Status */}
                                <div className="px-5 py-4 border-b border-slate-100">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="font-semibold text-slate-900 text-lg">
                                        {property.property_address || "Address TBC"}
                                      </h4>
                                    </div>
                                    <Badge className="bg-blue-500 text-white text-xs px-3 py-1">
                                      Sold
                                    </Badge>
                                  </div>
                                </div>
                                
                                {/* Property Details */}
                                <div className="px-5 py-4 space-y-3">
                                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                    <div className="flex">
                                      <span className="text-slate-500 w-16">Vendor</span>
                                      <span className="font-medium text-slate-900">{property.vendor_name || "-"}</span>
                                    </div>
                                    <div className="flex">
                                      <span className="text-slate-500 w-16">Agent</span>
                                      <span className="font-medium text-slate-900">{property.agent || "Goodacres"}</span>
                                    </div>
                                    <div className="flex">
                                      <span className="text-slate-500 w-16">Buyer</span>
                                      <span className="font-medium text-slate-900">{property.buyer_name || "-"}</span>
                                    </div>
                                    {property.price_agreed && (
                                      <div className="flex">
                                        <span className="text-slate-500 w-16">Price</span>
                                        <span className="font-medium text-green-600">£{Number(property.price_agreed).toLocaleString()}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Latest Note */}
                                  {property.notes && (
                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                      <div className="flex items-start justify-between">
                                        <div>
                                          <p className="text-xs font-semibold text-blue-700 mb-1">Latest Note:</p>
                                          <p className="text-sm text-slate-700">{property.notes}</p>
                                        </div>
                                        <button className="text-blue-600 text-xs font-medium hover:underline whitespace-nowrap ml-4">
                                          More Details &gt;
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => navigate(`/sales/case/${caseId}/chain`)}
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    data-testid={`view-chain-${idx}`}
                                  >
                                    View Details
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => navigate(`/sales/case/${caseId}/chain`)}
                                    data-testid={`edit-chain-${idx}`}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => handleDeleteChainProperty(property.chain_id)}
                                    data-testid={`delete-chain-${idx}`}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* FTB indicator at bottom if applicable */}
                        {chainProperties.length > 0 && chainProperties.some(p => p.buyer_chain_status === "ftb") && (
                          <div className="flex justify-center mt-4">
                            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                              <Search className="w-4 h-4 text-slate-500" />
                              <span className="text-sm font-medium text-slate-600">FTB</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Send Emails Panel in Chain Tab */}
                <div className="mt-4">
                  <SalesEmailPanel 
                    caseData={caseData} 
                    onEmailSent={() => fetchCaseData()}
                  />
                </div>
              </TabsContent>

              {/* Messages */}
              <TabsContent value="messages" className="mt-4 space-y-4">
                {/* Message Input */}
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      {/* Recipient Selection */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Send update to:</Label>
                        <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                          {/* Select All */}
                          <label className="flex items-center gap-3 p-2 rounded hover:bg-white cursor-pointer border-b pb-3">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={(e) => handleSelectAllRecipients(e.target.checked)}
                              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                            />
                            <span className="font-medium text-sm">Update All Parties</span>
                            <Badge variant="secondary" className="ml-auto text-xs">
                              {parties.filter(p => p.email).length} contacts
                            </Badge>
                          </label>
                          
                          {/* Individual Recipients */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pt-1">
                            {parties.filter(p => p.email).map((party) => {
                              const Icon = roleIcons[party.role] || User;
                              return (
                                <label 
                                  key={party.party_id}
                                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                                    selectedRecipients.includes(party.party_id) 
                                      ? "bg-primary/10 border border-primary/30" 
                                      : "hover:bg-white"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedRecipients.includes(party.party_id)}
                                    onChange={() => toggleRecipient(party.party_id)}
                                    className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                                  />
                                  <Icon className="w-4 h-4 text-slate-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{party.name}</p>
                                    <p className="text-xs text-muted-foreground">{roleLabels[party.role] || party.role}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                          
                          {parties.filter(p => p.email).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              No contacts with email addresses yet
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Message Input */}
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Message:</Label>
                        <Textarea
                          placeholder="Type your update here..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="w-full"
                          rows={3}
                        />
                      </div>

                      {/* Send Button */}
                      <Button 
                        className="w-full" 
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim() || selectedRecipients.length === 0}
                        data-testid="send-update-btn"
                      >
                        {sendingMessage ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send to {selectedRecipients.length} Recipient{selectedRecipients.length !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Message List */}
                <div className="space-y-3">
                  {messages.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center">
                        <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-muted-foreground">No messages yet</p>
                      </CardContent>
                    </Card>
                  ) : (
                    messages.map((msg) => (
                      <Card key={msg.message_id}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{msg.created_by_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {roleLabels[msg.created_by_role] || msg.created_by_role}
                                </Badge>
                              </div>
                              <p className="text-sm">{msg.body}</p>
                              {msg.recipient_names && msg.recipient_names.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  <Mail className="w-3 h-3 inline mr-1" />
                                  Sent to: {msg.recipient_names.join(", ")}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Documents */}
              <TabsContent value="documents" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    {/* Key Documents Section */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                          <FileCheck className="w-4 h-4" />
                          Key Documents
                        </h4>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleOpenTemplateModal}
                            data-testid="use-template-btn"
                          >
                            <FolderOpen className="w-4 h-4 mr-1" />
                            Use Template
                          </Button>
                          {!isSolicitor && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setShowEpcModal(true)}
                              data-testid="upload-epc-btn"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload EPC
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {/* EPC Document */}
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-amber-600" />
                              <span className="font-medium text-sm">EPC Certificate</span>
                            </div>
                            {caseData?.epc_band && (
                              <Badge className={`text-xs ${
                                ['A', 'B'].includes(caseData.epc_band) ? 'bg-green-100 text-green-700' :
                                ['C', 'D'].includes(caseData.epc_band) ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                Band {caseData.epc_band}
                              </Badge>
                            )}
                          </div>
                          {caseData?.epc_band ? (
                            <div className="space-y-1">
                              {caseData.epc_reference && (
                                <p className="text-xs text-slate-500">Ref: {caseData.epc_reference}</p>
                              )}
                              {caseData.epc_expiry && (
                                <p className="text-xs text-slate-500">Expires: {new Date(caseData.epc_expiry).toLocaleDateString('en-GB')}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                {documents.find(d => d.folder?.toLowerCase().includes('epc') || d.type === 'epc_certificate') ? (
                                  <a 
                                    href={documents.find(d => d.folder?.toLowerCase().includes('epc') || d.type === 'epc_certificate')?.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    View PDF
                                  </a>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 text-xs"
                                    onClick={() => setShowEpcModal(true)}
                                  >
                                    <Upload className="w-3 h-3 mr-1" />
                                    Upload PDF
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1">
                              <span className="text-xs text-slate-400">Not available</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 text-xs ml-2"
                                onClick={() => setShowEpcModal(true)}
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Add EPC
                              </Button>
                            </div>
                          )}
                        </div>
                        
                        {/* ID Verification */}
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-sm">ID Verification</span>
                            </div>
                            {documents.filter(d => 
                              d.type?.toLowerCase().includes('id') || 
                              d.folder?.toLowerCase().includes('id') || 
                              d.name?.toLowerCase().includes('id') ||
                              d.filename?.toLowerCase().includes('id')
                            ).length > 0 ? (
                              <span className="text-xs text-green-600">✓ Uploaded</span>
                            ) : (
                              <span className="text-xs text-slate-400">Not uploaded</span>
                            )}
                          </div>
                        </div>
                      </div>
                    
                    {/* Signed Contracts Section */}
                    {caseData?.contract_signed && (
                      <div className="mb-6 mt-4">
                        <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2 mb-3">
                          <Scale className="w-4 h-4" />
                          Signed Documents
                        </h4>
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <FileCheck className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm text-green-800">Agency Agreement</p>
                                <p className="text-xs text-green-600">
                                  Signed by seller • {caseData.contract_signed_at ? new Date(caseData.contract_signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Date unknown'}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-green-100 text-green-700">Signed ✓</Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Portal Uploaded Documents */}
                    {documents.filter(d => d.uploaded_by === 'seller' || d.uploaded_by === 'buyer').length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2 mb-3">
                          <Upload className="w-4 h-4" />
                          Client Uploads
                        </h4>
                        <div className="space-y-2">
                          {documents.filter(d => d.uploaded_by === 'seller' || d.uploaded_by === 'buyer').map((doc) => (
                            <div key={doc.document_id} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-purple-600" />
                                <div>
                                  <p className="font-medium text-sm">{doc.name || doc.type?.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-purple-600">
                                    Uploaded by {doc.uploaded_by} • {new Date(doc.created_at).toLocaleDateString('en-GB')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-purple-100 text-purple-700 capitalize">{doc.type?.replace(/_/g, ' ')}</Badge>
                                {doc.file_path && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewDocument(doc.document_id, doc.name)}
                                    data-testid={`view-doc-${doc.document_id}`}
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {documents.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-muted-foreground">No documents uploaded yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <div>
                                <p className="font-medium text-sm">{doc.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.folder} • Uploaded by {doc.uploaded_by_name}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{doc.folder}</Badge>
                              {doc.url && (
                                <a 
                                  href={doc.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  Download
                                </a>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(doc.document_id);
                                }}
                                data-testid={`delete-doc-${doc.document_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Activity Log */}
              <TabsContent value="activity" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    {auditLog.length === 0 ? (
                      <div className="text-center py-8">
                        <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-muted-foreground">No activity yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {auditLog.map((log) => (
                          <div key={log.log_id} className="flex items-start gap-3 text-sm">
                            <div className="w-2 h-2 rounded-full bg-slate-400 mt-2" />
                            <div>
                              <p>{(log.action || "").replace("_", " ")}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(log.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Parties */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Parties
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openAddPartyModal}
                      data-testid="add-party-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowApplicantSearch(true)}
                      data-testid="search-applicants-btn"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {parties.map((party) => {
                  const Icon = roleIcons[party.role] || User;
                  const isClickable = (party.role === "seller" && applicantIds.seller) || 
                                      (party.role === "buyer" && applicantIds.buyer);
                  const applicantId = party.role === "seller" ? applicantIds.seller : 
                                      party.role === "buyer" ? applicantIds.buyer : null;
                  
                  return (
                    <div key={party.party_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white border flex items-center justify-center">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          {isClickable ? (
                            <button
                              onClick={() => navigate(`/sales/applicant/${applicantId}`)}
                              className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                              data-testid={`party-link-${party.role}`}
                            >
                              {party.name}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          ) : (
                            <p className="font-medium text-sm">{party.name}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {roleLabels[party.role]}
                          </p>
                          {party.firm_name && (
                            <p className="text-xs text-muted-foreground">{party.firm_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditPartyModal(party)}
                          data-testid={`edit-${party.role}-btn`}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        {party.email && !party.portal_invited && !party.joined_at && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleInviteToPortal(party)}
                            data-testid={`invite-${party.role}-btn`}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Invite
                          </Button>
                        )}
                        {party.joined_at && (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        )}
                        {party.portal_invited && !party.joined_at && (
                          <Badge className="bg-amber-100 text-amber-700">Invited</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Invite Solicitor Button */}
                {!isSolicitor && (
                  <div className="pt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => setInviteSolicitorModal(true)}
                      data-testid="invite-solicitor-btn"
                    >
                      <Scale className="w-4 h-4 mr-2" />
                      Invite Solicitor to Portal
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Key Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Key Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Target Exchange</span>
                  <span className="font-medium">
                    {caseData.target_exchange_date || "Not set"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Target Completion</span>
                  <span className="font-medium">
                    {caseData.target_completion_date || "Not set"}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Sales Fee - Only visible to staff and sellers */}
            {caseData.sales_fee && canSeeSalesFee && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-emerald-700">
                    <PoundSterling className="w-4 h-4" />
                    Agency Fee
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-800">
                    £{Number(caseData.sales_fee).toLocaleString()}
                  </p>
                  {caseData.offer_price && caseData.sales_fee && (
                    <p className="text-xs text-emerald-600 mt-1">
                      {((caseData.sales_fee / caseData.offer_price) * 100).toFixed(2)}% of sale price
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sales Progression Notes */}
            {caseData.notes && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700">
                    <ClipboardList className="w-4 h-4" />
                    Sales Progression Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap">{caseData.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Waiting On */}
            {caseData.waiting_on_party_id && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Waiting on:</span>
                  </div>
                  <p className="text-amber-800 mt-1">
                    {parties.find(p => p.party_id === caseData.waiting_on_party_id)?.name || "Unknown"}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Email Panel for sending chase emails */}
            <SalesEmailPanel 
              caseData={caseData} 
              onEmailSent={() => fetchCaseData()}
            />
          </div>
        </div>
      </main>

      {/* Milestone Update Modal */}
      <Dialog open={milestoneModal} onOpenChange={setMilestoneModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Milestone</DialogTitle>
          </DialogHeader>
          
          {selectedMilestone && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium">{selectedMilestone.name}</p>
                <p className="text-sm text-muted-foreground">
                  Owner: {roleLabels[selectedMilestone.owner_role]}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={milestoneStatus} onValueChange={setMilestoneStatus} data-testid="milestone-status-select">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done ✓</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sub-items checkboxes for milestones like searches */}
              {selectedMilestone && hasSubItems(selectedMilestone.type) && (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium">
                    {selectedMilestone.type === "searches_back" ? "Searches Received" : "Items"}
                  </Label>
                  <div className="space-y-2">
                    {getSubItems(selectedMilestone.type).map((item) => (
                      <label 
                        key={item.id} 
                        className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={milestoneSubItems[item.id] || false}
                          onChange={() => toggleSubItem(item.id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-sm">{item.name}</span>
                        {item.forLeasehold && (
                          <Badge variant="outline" className="text-xs">Leasehold only</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                  {selectedMilestone.type === "searches_back" && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Status will auto-update: All 3 = Done, 1-2 = In Progress, 0 = Not Started
                    </p>
                  )}
                </div>
              )}

              {/* ETA Date - shown for in_progress and relevant milestones */}
              {showEtaOption(selectedMilestone) && milestoneStatus === "in_progress" && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-blue-600" />
                    Expected Completion Date (ETA)
                  </Label>
                  <Popover open={etaPopoverOpen} onOpenChange={setEtaPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="eta-date-picker"
                      >
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {milestoneEtaDate ? format(milestoneEtaDate, "dd MMM yyyy") : "Select ETA date..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={milestoneEtaDate}
                        onSelect={(date) => {
                          setMilestoneEtaDate(date);
                          setEtaPopoverOpen(false);
                        }}
                        initialFocus
                      />
                      {milestoneEtaDate && (
                        <div className="p-2 border-t">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full"
                            onClick={() => {
                              setMilestoneEtaDate(null);
                              setEtaPopoverOpen(false);
                            }}
                          >
                            Clear date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    When do you expect this to be completed?
                  </p>
                </div>
              )}

              {/* Scheduled Date - for survey/valuation bookings */}
              {showScheduledOption(selectedMilestone) && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    Scheduled/Booked Date
                  </Label>
                  <Popover open={scheduledPopoverOpen} onOpenChange={setScheduledPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="scheduled-date-picker"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {milestoneScheduledDate ? format(milestoneScheduledDate, "dd MMM yyyy") : "Select scheduled date..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={milestoneScheduledDate}
                        onSelect={(date) => {
                          setMilestoneScheduledDate(date);
                          setScheduledPopoverOpen(false);
                        }}
                        initialFocus
                      />
                      {milestoneScheduledDate && (
                        <div className="p-2 border-t">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full"
                            onClick={() => {
                              setMilestoneScheduledDate(null);
                              setScheduledPopoverOpen(false);
                            }}
                          >
                            Clear date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Date when survey/valuation is booked
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add any notes about this update..."
                  value={milestoneNotes}
                  onChange={(e) => setMilestoneNotes(e.target.value)}
                  data-testid="milestone-notes"
                />
              </div>

              {/* Notify Recipients - Hide for solicitors */}
              {!isSolicitor && (
              <div className="space-y-2">
                <Label>Notify parties about this update:</Label>
                <div className="border rounded-lg p-3 space-y-2 bg-slate-50 max-h-48 overflow-y-auto">
                  {/* Select All */}
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-white cursor-pointer border-b pb-2">
                    <input
                      type="checkbox"
                      checked={milestoneSelectAll}
                      onChange={(e) => handleMilestoneSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="font-medium text-sm">Notify All Parties</span>
                  </label>
                  
                  {/* Individual Recipients */}
                  {parties.filter(p => p.email).map((party) => {
                    const Icon = roleIcons[party.role] || User;
                    return (
                      <label 
                        key={party.party_id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          milestoneRecipients.includes(party.party_id) 
                            ? "bg-green-50 border border-green-200" 
                            : "hover:bg-white"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={milestoneRecipients.includes(party.party_id)}
                          onChange={() => toggleMilestoneRecipient(party.party_id)}
                          className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                        />
                        <Icon className="w-4 h-4 text-slate-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{party.name}</p>
                          <p className="text-xs text-muted-foreground">{roleLabels[party.role]}</p>
                        </div>
                      </label>
                    );
                  })}
                  
                  {parties.filter(p => p.email).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No contacts with email addresses
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Selected parties will receive an email notification
                </p>
              </div>
              )}
              
              {/* Greyed out notice for solicitors */}
              {isSolicitor && (
                <div className="space-y-2 opacity-50">
                  <Label className="text-slate-400">Email Notifications</Label>
                  <div className="border rounded-lg p-4 bg-slate-100 text-center">
                    <Mail className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Email notifications are managed by Goodacres</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMilestoneModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMilestone} disabled={updatingMilestone} data-testid="update-milestone-btn">
              {updatingMilestone ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Update {milestoneRecipients.length > 0 && `& Notify ${milestoneRecipients.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <Dialog open={inviteModal} onOpenChange={setInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invite</DialogTitle>
          </DialogHeader>
          
          {selectedParty && (
            <div className="py-4">
              <p>Send a magic link invite to:</p>
              <div className="bg-slate-50 rounded-lg p-4 mt-3">
                <p className="font-medium">{selectedParty.name}</p>
                <p className="text-sm text-muted-foreground">{selectedParty.email}</p>
                <Badge className="mt-2">{roleLabels[selectedParty.role]}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                They will receive an email with a secure link to access this case.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteParty} disabled={inviting}>
              {inviting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Solicitor Modal */}
      <Dialog open={inviteSolicitorModal} onOpenChange={setInviteSolicitorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              Invite Solicitor to Portal
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Solicitor Type</Label>
              <Select
                value={solicitorInviteData.type}
                onValueChange={(v) => setSolicitorInviteData({ ...solicitorInviteData, type: v })}
              >
                <SelectTrigger data-testid="solicitor-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seller">Seller&apos;s Solicitor</SelectItem>
                  <SelectItem value="buyer">Buyer&apos;s Solicitor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                placeholder="solicitor@lawfirm.com"
                value={solicitorInviteData.email}
                onChange={(e) => setSolicitorInviteData({ ...solicitorInviteData, email: e.target.value })}
                data-testid="solicitor-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="John Smith"
                value={solicitorInviteData.name}
                onChange={(e) => setSolicitorInviteData({ ...solicitorInviteData, name: e.target.value })}
                data-testid="solicitor-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Firm Name</Label>
              <Input
                placeholder="Smith & Partners LLP"
                value={solicitorInviteData.firm_name}
                onChange={(e) => setSolicitorInviteData({ ...solicitorInviteData, firm_name: e.target.value })}
                data-testid="solicitor-firm-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                placeholder="Optional phone number"
                value={solicitorInviteData.phone}
                onChange={(e) => setSolicitorInviteData({ ...solicitorInviteData, phone: e.target.value })}
                data-testid="solicitor-phone-input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-blue-700">
                <Scale className="w-4 h-4 inline mr-1" />
                The solicitor will receive a magic link email to access the Solicitor Portal
                where they can track milestones and update their progress.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteSolicitorModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInviteSolicitor}
              disabled={invitingSolicitor || !solicitorInviteData.email}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-solicitor-invite-btn"
            >
              {invitingSolicitor ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Removal Quote Modal */}
      <RemovalsQuoteModal
        open={quoteModal}
        onOpenChange={setQuoteModal}
        user={currentUser}
        cases={caseData ? [caseData] : []}
        showLinkedProperty={false}
      />

      {/* Applicant Search Modal */}
      <ApplicantSearch
        open={showApplicantSearch}
        onOpenChange={setShowApplicantSearch}
        onSelect={(applicant) => {
          navigate(`/sales/applicant/${applicant.applicant_id}`);
        }}
      />

      {/* Chain Property Modal */}
      <Dialog open={showChainModal} onOpenChange={setShowChainModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingChainProperty ? "Edit Chain Property" : "Add Property to Chain"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Property Address */}
            <div>
              <Label>Property Address</Label>
              <Input
                value={chainFormData.property_address}
                onChange={(e) => setChainFormData({...chainFormData, property_address: e.target.value})}
                placeholder="e.g., 123 High Street, Bedford"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Vendor Section */}
              <div className="space-y-3 p-3 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-800">Vendor Details</h4>
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={chainFormData.vendor_name}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_name: e.target.value})}
                    placeholder="Vendor name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={chainFormData.vendor_phone}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_phone: e.target.value})}
                    placeholder="Phone number"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={chainFormData.vendor_email}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_email: e.target.value})}
                    placeholder="Email address"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                
                <h4 className="font-semibold text-purple-800 pt-2 border-t">Vendor Solicitor</h4>
                <div>
                  <Label className="text-xs">Firm</Label>
                  <Input
                    value={chainFormData.vendor_solicitor_firm}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_solicitor_firm: e.target.value})}
                    placeholder="Solicitor firm name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact Name</Label>
                  <Input
                    value={chainFormData.vendor_solicitor_contact}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_solicitor_contact: e.target.value})}
                    placeholder="Contact name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={chainFormData.vendor_solicitor_phone}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_solicitor_phone: e.target.value})}
                    placeholder="Phone"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={chainFormData.vendor_solicitor_email}
                    onChange={(e) => setChainFormData({...chainFormData, vendor_solicitor_email: e.target.value})}
                    placeholder="Email"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>

              {/* Buyer Section */}
              <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800">Buyer Details</h4>
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={chainFormData.buyer_name}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_name: e.target.value})}
                    placeholder="Buyer name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={chainFormData.buyer_phone}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_phone: e.target.value})}
                    placeholder="Phone number"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={chainFormData.buyer_email}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_email: e.target.value})}
                    placeholder="Email address"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                
                <h4 className="font-semibold text-blue-800 pt-2 border-t">Buyer Solicitor</h4>
                <div>
                  <Label className="text-xs">Firm</Label>
                  <Input
                    value={chainFormData.buyer_solicitor_firm}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_solicitor_firm: e.target.value})}
                    placeholder="Solicitor firm name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact Name</Label>
                  <Input
                    value={chainFormData.buyer_solicitor_contact}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_solicitor_contact: e.target.value})}
                    placeholder="Contact name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={chainFormData.buyer_solicitor_phone}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_solicitor_phone: e.target.value})}
                    placeholder="Phone"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={chainFormData.buyer_solicitor_email}
                    onChange={(e) => setChainFormData({...chainFormData, buyer_solicitor_email: e.target.value})}
                    placeholder="Email"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Price, Agent, Status */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Price Agreed (£)</Label>
                <Input
                  type="number"
                  value={chainFormData.price_agreed}
                  onChange={(e) => setChainFormData({...chainFormData, price_agreed: e.target.value})}
                  placeholder="250000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Agent</Label>
                <Input
                  value={chainFormData.agent}
                  onChange={(e) => setChainFormData({...chainFormData, agent: e.target.value})}
                  placeholder="Estate agent"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Buyer Chain Status</Label>
                <Select
                  value={chainFormData.buyer_chain_status}
                  onValueChange={(value) => setChainFormData({...chainFormData, buyer_chain_status: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chain Free">Chain Free</SelectItem>
                    <SelectItem value="Under Offer">Under Offer</SelectItem>
                    <SelectItem value="Exchange Pending">Exchange Pending</SelectItem>
                    <SelectItem value="Exchanged">Exchanged</SelectItem>
                    <SelectItem value="On Market">On Market</SelectItem>
                    <SelectItem value="Not Yet On Market">Not Yet On Market</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Buyer Finance Status</Label>
              <Select
                value={chainFormData.buyer_finance_status}
                onValueChange={(value) => setChainFormData({...chainFormData, buyer_finance_status: value})}
              >
                <SelectTrigger className="mt-1 w-1/3">
                  <SelectValue placeholder="Select finance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash Buyer">Cash Buyer</SelectItem>
                  <SelectItem value="Mortgage - AIP">Mortgage - AIP</SelectItem>
                  <SelectItem value="Mortgage - Applied">Mortgage - Applied</SelectItem>
                  <SelectItem value="Mortgage - Offered">Mortgage - Offered</SelectItem>
                  <SelectItem value="First Time Buyer">First Time Buyer</SelectItem>
                  <SelectItem value="Investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={chainFormData.notes}
                onChange={(e) => setChainFormData({...chainFormData, notes: e.target.value})}
                placeholder="Add any notes about this property in the chain..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowChainModal(false); setEditingChainProperty(null); resetChainForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveChainProperty} disabled={savingChain}>
              {savingChain ? "Saving..." : (editingChainProperty ? "Update" : "Add to Chain")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EPC Upload Modal */}
      <Dialog open={showEpcModal} onOpenChange={setShowEpcModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Upload EPC Certificate
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* EPC Rating */}
            <div>
              <Label>EPC Rating *</Label>
              <Select
                value={epcData.rating}
                onValueChange={(v) => setEpcData({ ...epcData, rating: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select EPC band..." />
                </SelectTrigger>
                <SelectContent>
                  {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(band => (
                    <SelectItem key={band} value={band}>
                      Band {band} {band === 'A' ? '(Most efficient)' : band === 'G' ? '(Least efficient)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* EPC Reference */}
            <div>
              <Label>EPC Reference Number</Label>
              <Input
                value={epcData.reference}
                onChange={(e) => setEpcData({ ...epcData, reference: e.target.value })}
                placeholder="e.g. 0123-4567-8901-2345-6789"
                className="mt-1"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={epcData.expiry_date}
                onChange={(e) => setEpcData({ ...epcData, expiry_date: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* File Upload */}
            <div>
              <Label>EPC Certificate PDF *</Label>
              <div className="mt-1">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setEpcData({ ...epcData, file: e.target.files?.[0] || null })}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Only PDF files are accepted
                </p>
              </div>
              {epcData.file && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">{epcData.file.name}</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="ml-auto h-6 w-6 p-0"
                    onClick={() => setEpcData({ ...epcData, file: null })}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEpcModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEpcUpload} 
              disabled={uploadingEpc || !epcData.file || !epcData.rating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {uploadingEpc ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload EPC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-primary" />
              Create Document from Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Case Info Summary */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium">{caseData?.address_line1}</p>
              <p className="text-xs text-muted-foreground">{caseData?.postcode}</p>
              {caseData?.price && (
                <p className="text-xs text-primary mt-1">
                  £{Number(caseData.price).toLocaleString()}
                </p>
              )}
            </div>

            {/* Template Selection */}
            <Label className="mb-2 block">Select Template</Label>
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : solicitorTemplates.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No templates available</p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setShowTemplateModal(false);
                    navigate('/solicitor/templates');
                  }}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Upload Templates
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {solicitorTemplates.map(template => {
                  const isSelected = selectedTemplate?.template_id === template.template_id;
                  return (
                    <div
                      key={template.template_id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                          : 'hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded ${isSelected ? 'bg-primary text-white' : 'bg-slate-100'}`}>
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{template.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {template.original_filename}
                          </p>
                          {template.description && (
                            <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                              {template.description}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Data Preview */}
            {selectedTemplate && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-800 mb-2">
                  Document will include:
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-blue-700">
                  <span>Property: {caseData?.address_line1 || '—'}</span>
                  <span>Price: £{caseData?.price?.toLocaleString() || '—'}</span>
                  <span>Seller: {parties.find(p => p.role === 'seller')?.name || '—'}</span>
                  <span>Buyer: {parties.find(p => p.role === 'buyer')?.name || '—'}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUseTemplate} 
              disabled={generatingDoc || !selectedTemplate}
            >
              {generatingDoc ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Create Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Memo Preview Modal */}
      <Dialog open={showMemoPreview} onOpenChange={setShowMemoPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Review & Send Memos of Sale
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Review each memo below before sending. You can approve and send individually or all at once.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Seller Memo */}
            <div className={`border rounded-lg p-4 ${memoDrafts.seller.sent ? 'bg-green-50 border-green-200' : memoDrafts.seller.approved ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Seller: {memoDrafts.seller.name}</span>
                  {memoDrafts.seller.email && (
                    <span className="text-sm text-slate-500">({memoDrafts.seller.email})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {memoDrafts.seller.sent ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" /> Sent
                    </Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={memoDrafts.seller.approved ? "default" : "outline"}
                        onClick={() => setMemoDrafts(prev => ({
                          ...prev,
                          seller: { ...prev.seller, approved: !prev.seller.approved }
                        }))}
                      >
                        {memoDrafts.seller.approved ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                        {memoDrafts.seller.approved ? "Approved" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSendMemo("seller")}
                        disabled={!memoDrafts.seller.approved || !memoDrafts.seller.email || sendingMemo === "seller"}
                      >
                        {sendingMemo === "seller" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Textarea
                value={memoDrafts.seller.content}
                onChange={(e) => setMemoDrafts(prev => ({
                  ...prev,
                  seller: { ...prev.seller, content: e.target.value, approved: false }
                }))}
                rows={6}
                className="text-sm font-mono"
                disabled={memoDrafts.seller.sent}
              />
            </div>

            {/* Buyer Memo */}
            <div className={`border rounded-lg p-4 ${memoDrafts.buyer.sent ? 'bg-green-50 border-green-200' : memoDrafts.buyer.approved ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Buyer: {memoDrafts.buyer.name}</span>
                  {memoDrafts.buyer.email && (
                    <span className="text-sm text-slate-500">({memoDrafts.buyer.email})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {memoDrafts.buyer.sent ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" /> Sent
                    </Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={memoDrafts.buyer.approved ? "default" : "outline"}
                        onClick={() => setMemoDrafts(prev => ({
                          ...prev,
                          buyer: { ...prev.buyer, approved: !prev.buyer.approved }
                        }))}
                      >
                        {memoDrafts.buyer.approved ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                        {memoDrafts.buyer.approved ? "Approved" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSendMemo("buyer")}
                        disabled={!memoDrafts.buyer.approved || !memoDrafts.buyer.email || sendingMemo === "buyer"}
                      >
                        {sendingMemo === "buyer" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Textarea
                value={memoDrafts.buyer.content}
                onChange={(e) => setMemoDrafts(prev => ({
                  ...prev,
                  buyer: { ...prev.buyer, content: e.target.value, approved: false }
                }))}
                rows={6}
                className="text-sm font-mono"
                disabled={memoDrafts.buyer.sent}
              />
            </div>

            {/* Seller's Solicitor Memo */}
            <div className={`border rounded-lg p-4 ${memoDrafts.sellerSolicitor.sent ? 'bg-green-50 border-green-200' : memoDrafts.sellerSolicitor.approved ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Seller&apos;s Solicitor: {memoDrafts.sellerSolicitor.name}</span>
                  {memoDrafts.sellerSolicitor.firm && (
                    <span className="text-sm text-slate-500">({memoDrafts.sellerSolicitor.firm})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {memoDrafts.sellerSolicitor.sent ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" /> Sent
                    </Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={memoDrafts.sellerSolicitor.approved ? "default" : "outline"}
                        onClick={() => setMemoDrafts(prev => ({
                          ...prev,
                          sellerSolicitor: { ...prev.sellerSolicitor, approved: !prev.sellerSolicitor.approved }
                        }))}
                      >
                        {memoDrafts.sellerSolicitor.approved ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                        {memoDrafts.sellerSolicitor.approved ? "Approved" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSendMemo("sellerSolicitor")}
                        disabled={!memoDrafts.sellerSolicitor.approved || !memoDrafts.sellerSolicitor.email || sendingMemo === "sellerSolicitor"}
                      >
                        {sendingMemo === "sellerSolicitor" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Textarea
                value={memoDrafts.sellerSolicitor.content}
                onChange={(e) => setMemoDrafts(prev => ({
                  ...prev,
                  sellerSolicitor: { ...prev.sellerSolicitor, content: e.target.value, approved: false }
                }))}
                rows={8}
                className="text-sm font-mono"
                disabled={memoDrafts.sellerSolicitor.sent}
              />
            </div>

            {/* Buyer's Solicitor Memo */}
            <div className={`border rounded-lg p-4 ${memoDrafts.buyerSolicitor.sent ? 'bg-green-50 border-green-200' : memoDrafts.buyerSolicitor.approved ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">Buyer&apos;s Solicitor: {memoDrafts.buyerSolicitor.name}</span>
                  {memoDrafts.buyerSolicitor.firm && (
                    <span className="text-sm text-slate-500">({memoDrafts.buyerSolicitor.firm})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {memoDrafts.buyerSolicitor.sent ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" /> Sent
                    </Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={memoDrafts.buyerSolicitor.approved ? "default" : "outline"}
                        onClick={() => setMemoDrafts(prev => ({
                          ...prev,
                          buyerSolicitor: { ...prev.buyerSolicitor, approved: !prev.buyerSolicitor.approved }
                        }))}
                      >
                        {memoDrafts.buyerSolicitor.approved ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                        {memoDrafts.buyerSolicitor.approved ? "Approved" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSendMemo("buyerSolicitor")}
                        disabled={!memoDrafts.buyerSolicitor.approved || !memoDrafts.buyerSolicitor.email || sendingMemo === "buyerSolicitor"}
                      >
                        {sendingMemo === "buyerSolicitor" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Textarea
                value={memoDrafts.buyerSolicitor.content}
                onChange={(e) => setMemoDrafts(prev => ({
                  ...prev,
                  buyerSolicitor: { ...prev.buyerSolicitor, content: e.target.value, approved: false }
                }))}
                rows={8}
                className="text-sm font-mono"
                disabled={memoDrafts.buyerSolicitor.sent}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex-1 text-sm text-slate-500">
              {Object.values(memoDrafts).filter(d => d.sent).length} of 4 memos sent
            </div>
            <Button variant="outline" onClick={() => setShowMemoPreview(false)}>
              Close
            </Button>
            <Button
              onClick={handleSendAllMemos}
              disabled={!Object.values(memoDrafts).some(d => d.approved && !d.sent && d.email)}
            >
              <Send className="w-4 h-4 mr-2" />
              Send All Approved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Party Modal */}
      <Dialog open={showPartyModal} onOpenChange={setShowPartyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingParty ? "Edit Party" : "Add Party"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="party-role">Role *</Label>
              <Select
                value={partyForm.role}
                onValueChange={(value) => setPartyForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="party-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="seller">Seller</SelectItem>
                  <SelectItem value="buyer_solicitor">Buyer&apos;s Solicitor</SelectItem>
                  <SelectItem value="seller_solicitor">Seller&apos;s Solicitor</SelectItem>
                  <SelectItem value="broker">Mortgage Broker</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="party-name">Name *</Label>
              <Input
                id="party-name"
                value={partyForm.name}
                onChange={(e) => setPartyForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
                data-testid="party-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="party-email">Email</Label>
              <Input
                id="party-email"
                type="email"
                value={partyForm.email}
                onChange={(e) => setPartyForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email address"
                data-testid="party-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="party-phone">Phone</Label>
              <Input
                id="party-phone"
                type="tel"
                value={partyForm.phone}
                onChange={(e) => setPartyForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter phone number"
                data-testid="party-phone-input"
              />
            </div>
            {(partyForm.role === "buyer_solicitor" || partyForm.role === "seller_solicitor") && (
              <div className="space-y-2">
                <Label htmlFor="party-firm">Firm Name</Label>
                <Input
                  id="party-firm"
                  value={partyForm.firm_name}
                  onChange={(e) => setPartyForm(prev => ({ ...prev, firm_name: e.target.value }))}
                  placeholder="Enter law firm name"
                  data-testid="party-firm-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPartyModal(false)}
              data-testid="party-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveParty}
              disabled={savingParty || !partyForm.name}
              data-testid="party-save-btn"
            >
              {savingParty ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {editingParty ? "Update Party" : "Add Party"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

